import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { mobileMenuLinks, mobileTreatmentLinks } from "@/mocks/navigation";
import { clinic } from "@/mocks/clinic";
import { LineButton } from "./LineButton";

type MobileMenuProps = {
  open: boolean;
  onClose: () => void;
};

export default function MobileMenu({ open, onClose }: MobileMenuProps) {
  const [treatmentOpen, setTreatmentOpen] = useState(true);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <div
      className={`fixed inset-0 z-[80] lg:hidden ${open ? "" : "pointer-events-none"}`}
      aria-hidden={!open}
    >
      <button
        type="button"
        aria-label="メニューを閉じる"
        onClick={onClose}
        className={`absolute inset-0 bg-primary-950/50 transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0"
        }`}
      />
      <div
        className={`absolute right-0 top-0 flex h-full w-full max-w-sm flex-col bg-primary-700 transition-transform duration-500 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-6 py-5">
          <span className="font-label text-xl tracking-[0.2em] text-background-50">SBDC</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="閉じる"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-background-50/25 text-background-50"
          >
            <i className="ri-close-line text-xl" aria-hidden />
          </button>
        </div>

        <div className="no-scrollbar flex-1 overflow-y-auto px-6 pb-8">
          <div className="border-t border-background-50/15">
            <button
              type="button"
              onClick={() => setTreatmentOpen((prev) => !prev)}
              className="flex w-full items-center justify-between py-4 text-left text-background-50"
            >
              <span className="font-heading text-[15px]">施術メニュー</span>
              <i className={`ri-arrow-down-s-line text-xl transition-transform ${treatmentOpen ? "rotate-180" : ""}`} aria-hidden />
            </button>
            <div className={`overflow-hidden transition-all duration-300 ${treatmentOpen ? "max-h-96 pb-3" : "max-h-0"}`}>
              <ul className="space-y-1 pl-1">
                {mobileTreatmentLinks.map((item) => (
                  <li key={item.label}>
                    <Link
                      to={item.href}
                      onClick={onClose}
                      className="flex items-center gap-3 py-2.5 text-[13px] text-background-200 transition-colors hover:text-background-50"
                    >
                      <span className="h-px w-3 bg-accent-400" />
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <ul className="border-t border-background-50/15">
            {mobileMenuLinks.map((item) => {
              const isExternal = item.href.startsWith("http");
              return (
                <li key={item.label} className="border-b border-background-50/15">
                  {isExternal ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={onClose}
                      className="flex items-center justify-between py-4 text-[15px] text-background-50"
                    >
                      <span className="font-heading">{item.label}</span>
                      <i className="ri-arrow-right-s-line text-lg text-background-200" aria-hidden />
                    </a>
                  ) : (
                    <Link
                      to={item.href}
                      onClick={onClose}
                      className="flex items-center justify-between py-4 text-[15px] text-background-50"
                    >
                      <span className="font-heading">{item.label}</span>
                      <i className="ri-arrow-right-s-line text-lg text-background-200" aria-hidden />
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>

          <div className="mt-8 flex flex-col gap-3">
            <a
              href={clinic.webReserveUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
              className="block w-full rounded-md bg-background-50 py-3.5 text-center text-[15px] font-medium text-primary-700"
            >
              WEB予約
            </a>
            <LineButton
              onClick={onClose}
              className="flex w-full items-center justify-center gap-2 rounded-md border border-background-50/40 py-3.5 text-[15px] text-background-50"
            >
              <i className="ri-line-fill text-lg" aria-hidden />
              LINEで相談
            </LineButton>
            <a
              href={clinic.telHref}
              onClick={onClose}
              className="flex w-full items-center justify-center gap-2 py-2 text-[13px] text-background-200"
            >
              <i className="ri-phone-line" aria-hidden />
              {clinic.tel}（{clinic.hours}）
            </a>
          </div>

          <div className="mt-8 text-[11px] leading-relaxed text-background-200/80">
            {clinic.postal}
            <br />
            {clinic.address}
          </div>
        </div>
      </div>
    </div>
  );
}